"use client"
import { atom, useAtom } from "jotai"
import { useHydrateAtoms } from "jotai/utils"
import { AiGeneratedPostValues, Blog, Note } from "@types"

const shouldSkipUpdateAtom = atom<boolean>(false)
const isCreatingDraftAtom = atom<boolean>(false)
const queuedUpdateObjectAtom = atom<Partial<Note>>({})
const blogAtom = atom<Blog>({
  id: "",
  name: "",
  lowercase_url: "",
  url: "",
  createdAt: Date.now(),
})
const postIdAtom = atom<string>("")
const isPostPreviewManualAtom = atom<boolean>(false)
const publishedAtom = atom<boolean | undefined>(false)
const draftOfAtom = atom<string>("")
const postPreviewAtom = atom<string | undefined>(undefined)
const aiGeneratedAtom = atom<AiGeneratedPostValues | undefined>()
const titleAtom = atom<string>("Untitled Post")
const subtitleAtom = atom<string>("")
const coverImageAtom = atom<any | null>(null)
const saveActionAtom = atom<string>("Draft Saved")
const isSavingAtom = atom<boolean>(false)

import { ScopeProvider } from "jotai-scope"

/**
 * Functionality used on the "post edit" routes.
 *
 * Handles persisting data for the post being edited, into
 * the publish page, the sheet, etc.
 *
 * Enables interactivity without prop-drilling.
 */
export function usePostEdit() {
  const [shouldSkipUpdate, setShouldSkipUpdate] = useAtom(shouldSkipUpdateAtom)
  const [isCreatingDraft, setIsCreatingDraft] = useAtom(isCreatingDraftAtom)
  const [queuedUpdateObject, setQueuedUpdateObject] = useAtom(
    queuedUpdateObjectAtom
  )
  const [aiGenerated, setAiGenerated] = useAtom(aiGeneratedAtom)
  const [blog] = useAtom(blogAtom)
  const [postId, setPostId] = useAtom(postIdAtom)
  const [published, setPublished] = useAtom(publishedAtom)
  const [draftOf, setDraftOf] = useAtom(draftOfAtom)
  const [isPostPreviewManual, setIsPostPreviewManual] = useAtom(
    isPostPreviewManualAtom
  )
  const [postPreview, setPostPreview] = useAtom(postPreviewAtom)
  const [title, setTitle] = useAtom(titleAtom)
  const [subtitle, setSubtitle] = useAtom(subtitleAtom)
  const [coverImage, setCoverImage] = useAtom(coverImageAtom)
  const [saveAction, setSaveAction] = useAtom(saveActionAtom)
  const [isSaving, setIsSaving] = useAtom(isSavingAtom)

  const data = {
    shouldSkipUpdate,
    setShouldSkipUpdate,
    isCreatingDraft,
    setIsCreatingDraft,
    queuedUpdateObject,
    setQueuedUpdateObject,
    aiGenerated,
    setAiGenerated,
    blog,
    postId,
    setPostId,
    published,
    setPublished,
    draftOf,
    setDraftOf,
    isPostPreviewManual,
    setIsPostPreviewManual,
    postPreview,
    setPostPreview,
    title,
    setTitle,
    subtitle,
    setSubtitle,
    coverImage,
    setCoverImage,
    saveAction,
    setSaveAction,
    isSaving,
    setIsSaving,
  }

  //console.log("OMG: usePostEdit", data)

  return data
}

function HydratePostEdits({
  children,
  blog,
  post,
}: {
  children: React.ReactNode
  blog: Blog
  post: Note
}) {
  useHydrateAtoms([
    [shouldSkipUpdateAtom, false],
    [isCreatingDraftAtom, false],
    [queuedUpdateObjectAtom, {}],
    [aiGeneratedAtom, post.aiGenerated],
    [blogAtom, blog],
    [postIdAtom, post.id],
    [publishedAtom, post.published],
    [draftOfAtom, post.draftOf || ""],
    [isPostPreviewManualAtom, post.isPostPreviewManual || false],
    [postPreviewAtom, post.post_preview],
    [titleAtom, post.title || "Untitled Post"],
    [subtitleAtom, post.subtitle || ""],
    [coverImageAtom, post.cover_img],
  ])

  return <> {children} </>
}

export function PostEditProvider({
  children,
  blog,
  post,
}: {
  children: React.ReactNode
  blog: Blog
  post: Note
}) {
  // console.log("OMG: PostEditProvider", post)

  return (
    <ScopeProvider
      atoms={[
        shouldSkipUpdateAtom,
        isCreatingDraftAtom,
        queuedUpdateObjectAtom,
        blogAtom,
        postIdAtom,
        publishedAtom,
        draftOfAtom,
        isPostPreviewManualAtom,
        postPreviewAtom,
        titleAtom,
        subtitleAtom,
        coverImageAtom,
        isSavingAtom,
      ]}
      key={JSON.stringify(post)}
    >
      <HydratePostEdits blog={blog} post={post}>
        {children}
      </HydratePostEdits>
    </ScopeProvider>
  )
}
